import getRecommendationsApi from "api/home/getRecommendationsApi";
import NextImage from "component/design-system/images/NextImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import HideIf from "util/HideIf";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import GenericRecommendationCard from "./GenericRecommendationCard";
import styles from "./recommendation-cards.module.scss";

interface CardItem {
  label: string;
  img: string;
  link: string;
}

interface CardData {
  title: string;
  subtitle?: string;
  className: string;
  type?: string;
  key?: string;
  icon?: string;
  items: CardItem[];
}

const CARD_STYLES: Record<string, { background: string; border: string }> = {
  on_sale: {
    background: "rgba(255, 241, 242, 0.4)",
    border: "2px solid rgb(255, 228, 230)",
  },
  weather_based: {
    background: "rgba(239, 246, 255, 0.4)",
    border: "2px solid rgb(219, 234, 254)",
  },
  vehicle_based: {
    background: "rgba(240, 253, 244, 0.3)",
    border: "2px solid rgb(220, 252, 231)",
  },
  seasonal: {
    background: "rgba(236, 254, 255, 0.4)",
    border: "2px solid rgb(207, 250, 254)",
  },
};

const HomeRecommendationCards = () => {
  const [cardData, setCardData] = useState<CardData[]>([]);
  const [loading, setLoading] = useState(true);

  const vehicles = useSelector((state: RootState) => state.garage.vehicles);
  const { refreshStatus, loggedIn } = useSelector((store: RootState) => store.authenticationState);
  const { postalCode, hasRefreshed } = useSelector((store: RootState) => store.shoppingCart);
  const { locale } = useRouter();

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);

        // Transform vehicles to match API payload format
        const vehiclesPayload = vehicles.map((vehicle) => ({
          make: vehicle.make,
          model: vehicle.model,
          year: parseInt(vehicle.year, 10),
        })).slice(0, 1);

        const response = await getRecommendationsApi({
          page_type: "HOME_PAGE",
          is_logged_in: loggedIn,
          fsa: postalCode?.substring(0, 3), // forward sortation area (first 3 characters of postal code)
          vehicles: vehiclesPayload,
          locale: locale ?? "en",
        });

        // Transform API response to match component structure
        const transformedData: CardData[] = response.data.recommendation_groups.map((group) => ({
          title: group.title,
          subtitle: group.subtitle,
          className: "",
          key: group.key,
          icon: group.icon,
          items: group.items.map((item) => ({
            label: item.description,
            img: item.img_link,
            link: item.page_link,
          })),
        }));

        setCardData(transformedData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch recommendations:", error);
      } finally {
        setLoading(false);
      }
    };

    if (refreshStatus === "complete" && hasRefreshed) {
      fetchRecommendations();
    }
  }, [loggedIn, vehicles, refreshStatus, hasRefreshed, locale]);

  if (loading) {
    return (
      <div className={styles.recommendation_loader_skeleton}>
        <LoaderSkeleton height={520} />
        <LoaderSkeleton height={520} />
        <LoaderSkeleton height={520} />
        <LoaderSkeleton height={520} />
      </div>
    );
  }

  if (!cardData.length) {
    return null;
  }

  return (
    <div className={styles.recommendation_wrapper}>
      <PAText component="h2" fontSize={FONT_SIZE[5]} className="m-t-0 m-b-15" bold>Just For You</PAText>
      <div className={styles.recommendationContainer}>
        {cardData.slice(0, 4).map((card) => {
          const cardStyle = CARD_STYLES[card.key || ""] || CARD_STYLES.on_sale;
          return (
            <div key={card.key || card.title} className={`${styles.card} ${card.className}`} style={{ background: cardStyle.background, border: cardStyle.border }}>
              <div className={styles.card_header}>
                <HideIf show={card.icon}>
                  <NextImage
                    src={getRelativeImagePath(`/cms/pubicImages/images/${card.icon}`)}
                    width={25}
                    height={25}
                    alt={card.title}
                    className={styles.card_icon}
                  />
                </HideIf>
                <div className={styles.card_header_text}>
                  <PAText component="h3" className="m-t-0 m-b-0" fontSize={FONT_SIZE[1]} color="black" bold>{card.title}</PAText>
                  {card.subtitle && <PAText component="p" fontSize={FONT_SIZE[0]} color="grey">{card.subtitle}</PAText>}
                </div>
              </div>

              <GenericRecommendationCard card={card} />

              {/* <Link href="/view-all">
                See more
              </Link> */}
            </div>
          );
        })}
        {/* <ShoppingCartCard /> */}
      </div>
    </div>
  );
};

export default HomeRecommendationCards;
